import { type ReactNode, useEffect, useMemo } from 'react';
import {
  type MetaDescriptor,
  type Params,
  useLoaderData,
  useParams,
  useRevalidator,
} from 'react-router';

import { LoadingSection } from 'components/LoadingSection/LoadingSection';
import { useIsFirstRenderRef } from 'hooks/useIsFirstRenderRef';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import type { HorizonLoaderFunction } from 'routing/types/HorizonLoaderFunction';
import type { HorizonRouteMetaArgs } from 'routing/types/HorizonRouteMetaArgs';

import { renderClientOnlyLoaderToken } from './renderClientOnlyLoaderToken';

type Props<TData> = {
  meta:
    | ((args: HorizonRouteMetaArgs<HorizonLoaderFunction>) => MetaDescriptor[])
    | undefined;
  render: (data: TData, params: Params<string>) => ReactNode;
};

export function RenderLoaderComponent<TData>({ meta, render }: Props<TData>) {
  const loaderData = useLoaderData() as TData;
  const params = useParams();

  const revalidator = useRevalidator();

  const shouldRevalidate = loaderData === renderClientOnlyLoaderToken;
  const { isFirstRenderRef } = useIsFirstRenderRef();

  const metaTitle: string | undefined = useMemo(() => {
    const metaDescriptors =
      meta?.({
        data: loaderData,
        params,
      }) || [];

    for (const descriptor of metaDescriptors) {
      if ('title' in descriptor && typeof descriptor.title === 'string')
        return descriptor.title;
    }
  }, [meta, loaderData, params]);

  useSetDocumentTitle(metaTitle);

  useEffect(() => {
    if (shouldRevalidate && isFirstRenderRef.current)
      void revalidator.revalidate();
  }, [isFirstRenderRef, revalidator, shouldRevalidate]);

  if (shouldRevalidate) return <LoadingSection />;

  return render(loaderData, params);
}
